<template>
  <div class="grid w-full gap-4 p-6">
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2
        class="text-lg font-semibold leading-none tracking-tight flex items-center gap-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-bell h-5 w-5"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
        </svg>
        <!-- {{ rule?.ruleName || 'Alert Rule' }} -->
      </h2>
    </div>

    <div class="space-y-6 py-4" v-if="rule">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          class="rounded-lg border bg-card text-card-foreground shadow-2xs"
          data-v0-t="card"
        >
          <div class="p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-muted-foreground">Status</p>
                <p class="text-2xl font-bold capitalize">{{ statusLabel }}</p>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-circle-check h-8 w-8"
                :class="statusIconClass"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="m9 12 2 2 4-4"></path>
              </svg>
            </div>
          </div>
        </div>
        <div
          class="rounded-lg border bg-card text-card-foreground shadow-2xs"
          data-v0-t="card"
        >
          <div class="p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-muted-foreground">Total Triggers</p>
                <p class="text-2xl font-bold">{{ totalTriggers }}</p>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-triangle-alert h-8 w-8 text-orange-500"
              >
                <path
                  d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
                ></path>
                <path d="M12 9v4"></path>
                <path d="M12 17h.01"></path>
              </svg>
            </div>
          </div>
        </div>
        <div
          class="rounded-lg border bg-card text-card-foreground shadow-2xs"
          data-v0-t="card"
        >
          <div class="p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-muted-foreground">Last Triggered</p>
                <p class="text-sm font-medium">{{ lastTriggerLabel }}</p>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-clock h-8 w-8 text-blue-500"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>

      <div class="space-y-4">
        <h3 class="font-semibold flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-settings h-4 w-4"
          >
            <path
              d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
            ></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          Configuration
        </h3>
        <div class="grid gap-4 md:grid-cols-2">
          <div class="space-y-1">
            <label class="text-sm font-medium text-muted-foreground"
              >Event Source(s)</label
            >
            <div v-if="eventSources.length" class="flex flex-wrap gap-1">
              <div
                v-for="source in eventSources"
                :key="source"
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold text-foreground capitalize"
              >
                {{ source }}
              </div>
            </div>
            <p v-else class="text-sm text-muted-foreground">
              No devices configured.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm text-muted-foreground">Level:</span>
            <div class="flex flex-wrap gap-1">
              <div
                v-for="level in eventLevelLabels"
                :key="level"
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-blue-500/10 text-blue-500 border-blue-500/20"
              >
                {{ level }}
              </div>
              <span
                v-if="!eventLevelLabels.length"
                class="text-sm text-muted-foreground"
                >--</span
              >
            </div>
          </div>
          <div class="space-y-1">
            <label class="text-sm font-medium text-muted-foreground"
              >Created</label
            >
            <p class="font-medium">{{ createdLabel }}</p>
          </div>
          <div class="space-y-1">
            <label class="text-sm font-medium text-muted-foreground"
              >Cooldown Scope</label
            >
            <p class="font-medium">{{ cooldownScopeLabel }}</p>
          </div>
          <div class="space-y-1">
            <label class="text-sm font-medium text-muted-foreground"
              >Rule ID</label
            >
            <p class="font-mono text-sm">{{ rule?.rule_id ?? "--" }}</p>
          </div>
        </div>
      </div>

      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>

      <div class="space-y-4">
        <h3 class="font-semibold flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-users h-4 w-4"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          Primary Recipients ({{ primaryRecipientCount }})
        </h3>
        <div v-if="recipientList.length" class="grid gap-2">
          <div
            v-for="recipient in recipientList"
            :key="recipient"
            class="flex items-center justify-between p-3 rounded-lg border"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-user h-5 w-5 text-primary"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="10" cy="7" r="4"></circle>
                </svg>
              </div>
              <div>
                <p class="font-medium">{{ recipient }}</p>
                <p class="text-sm text-muted-foreground">Recipient</p>
              </div>
            </div>
            <span class="text-xs text-slate-400">Notification</span>
          </div>
        </div>
        <p v-else class="text-sm text-muted-foreground">
          No recipients assigned.
        </p>
      </div>

      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>

      <div class="space-y-4">
        <h3 class="font-semibold flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-circle-arrow-up h-4 w-4"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m16 12-4-4-4 4"></path>
            <path d="M12 16V8"></path>
          </svg>
          Escalation Rules
        </h3>
        <div v-if="escalationCards.length" class="space-y-3">
          <div
            v-for="block in escalationCards"
            :key="block.level"
            class="rounded-lg border bg-card text-card-foreground shadow-2xs border-l-4 border-l-orange-500"
            data-v0-t="card"
          >
            <div class="p-6 pt-4">
              <div class="flex items-start justify-between">
                <div>
                  <div class="flex items-center gap-2 mb-2">
                    <div
                      class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-orange-50 text-orange-700 border-orange-200"
                    >
                      Level {{ block.level }}
                    </div>
                    <span
                      class="text-sm text-muted-foreground flex items-center gap-1"
                      v-if="block.waitMin"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-clock h-3 w-3"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      After {{ block.waitMin }} minutes without acknowledgment
                    </span>
                  </div>
                  <div class="space-y-1">
                    <label class="font-medium text-xs text-muted-foreground"
                      >Escalate to:</label
                    >
                    <div class="flex flex-wrap gap-2">
                      <div
                        v-for="recipient in block.recipients"
                        :key="recipient"
                        class="flex items-center gap-2 text-sm"
                      >
                        <div
                          class="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-user h-3 w-3 text-orange-600"
                          >
                            <path
                              d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                            ></path>
                            <circle cx="10" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <span class="font-medium">{{ recipient }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-muted-foreground">
          Escalation is not configured for this rule.
        </p>
      </div>

      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      />

      <div class="space-y-4">
        <h3 class="font-semibold flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-message-square h-4 w-4"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            ></path>
          </svg>
          Message Template
        </h3>
        <div
          class="rounded-lg border text-card-foreground shadow-2xs bg-muted/50"
          data-v0-t="card"
        >
            <div class="p-6 pt-4">
            <pre
              class="whitespace-pre-wrap text-sm font-mono break-words max-w-full"
              style="word-break: break-all; overflow-x: auto;"
            >{{ rule.template || "No template configured." }}</pre>
            </div>
        </div>
        <div class="space-y-2">
          <label class="font-medium text-sm text-muted-foreground"
            >Preview with sample data:</label
          >
          <div
            class="rounded-lg border text-card-foreground shadow-2xs bg-gray-900 border-gray-700"
            data-v0-t="card"
          >
            <div class="p-6 pt-4">
              <div class="flex items-start gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-message-square h-5 w-5 text-green-500 mt-0.5"
                >
                  <path
                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  ></path>
                </svg>
                <pre class="whitespace-pre-wrap text-sm break-words max-w-full" style="word-break: break-all; overflow-x: auto;">
                  {{ templatePreview || "Preview unavailable." }}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      />

      <div class="space-y-4">
        <h3 class="font-semibold flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-history h-4 w-4"
          >
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
            <path d="M3 3v5h5"></path>
            <path d="M12 7v5l4 2"></path>
          </svg>
          Recent Alerts
        </h3>
        <div class="relative" style="height: 320px">
          <SmartInfiniteCursorScroll
            :loadMethod="listNotifications"
            :pageSize="10"
            listField="notificationList"
            :queryParams="{ rule_id: rule?.rule_id }"
            class="absolute inset-0"
            ref="smartInfiniteCursorScrollRef"
          >
            <template #default="{ item }">
              <div
                class="flex items-center justify-between p-3 rounded-lg border mb-2"
              >
                <div class="flex items-center gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-activity h-4 w-4"
                    :class="historyIconClass(item.status)"
                  >
                    <polyline
                      points="22 12 18 12 15 21 9 3 6 12 2 12"
                    ></polyline>
                  </svg>
                  <div>
                    <p class="text-sm font-medium">
                      {{
                        item.params?.cameraName ||
                        item.content ||
                        item.rule_name ||
                        "Alert"
                      }}
                    </p>
                    <p class="text-xs text-muted-foreground">
                      {{ item.rule_name || "--" }}
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <div
                    class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold"
                    :class="statusBadgeClass(item.status)"
                  >
                    {{ item.status || "--" }}
                  </div>
                  <p class="text-xs text-muted-foreground mt-1">
                    {{ formatTimestamp(item.timestamp) }}
                  </p>
                </div>
              </div>
            </template>
          </SmartInfiniteCursorScroll>
        </div>
      </div>
    </div>

    <p v-else class="text-sm text-muted-foreground py-6 text-center">
      Unable to load rule information. Please try again.
    </p>

    <div
      class="flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 flex justify-between"
    >
      <el-button @click="$emit('cancel')"> Close </el-button>
    </div>
    <button
      @click="$emit('cancel')"
      type="button"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-x h-4 w-4"
      >
        <path d="M18 6 6 18"></path>
        <path d="m6 6 12 12"></path>
      </svg>
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>

<script>
import SmartInfiniteCursorScroll from "./SmartInfiniteCursorScroll.vue";
import { listNotifications } from "@/api";

export default {
  name: "DetailWhatsAppAlert",
  components: {
    SmartInfiniteCursorScroll,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      smartInfiniteCursorScrollRef: null,
    };
  },
  computed: {
    rule() {
      return this.item;
    },
    statusLabel() {
      if (!this.rule) return "--";
      return this.rule.enabled ? "active" : "disabled";
    },
    statusIconClass() {
      if (!this.rule || !this.rule.enabled) {
        return "text-gray-400";
      }
      return "text-green-500";
    },
    totalTriggers() {
      return this.rule && typeof this.rule.priority === "number"
        ? this.rule.priority
        : 0;
    },
    lastTriggerLabel() {
      if (this.rule && this.rule.created_at) {
        return this.formatTimestamp(this.rule.created_at);
      }
      return "--";
    },
    eventSources() {
      if (!this.rule) return [];
      const match = this.rule.params_match || {};
      if (Array.isArray(match.deviceId) && match.deviceId.length) {
        return match.deviceId;
      }
      if (Array.isArray(match.triggerType) && match.triggerType.length) {
        return match.triggerType;
      }
      return [];
    },
    eventLevelLabels() {
      if (this.rule && typeof this.rule.priority === "number") {
        return [String(this.rule.priority)];
      }
      return [];
    },
    createdLabel() {
      return this.rule && this.rule.created_at
        ? this.formatTimestamp(this.rule.created_at)
        : "--";
    },
    cooldownScopeLabel() {
      if (this.rule?.cooldown_by_param === "deviceKey") {
        return "Per device (deviceKey)";
      }
      if (this.rule?.cooldown_by_param) {
        return `By ${this.rule.cooldown_by_param}`;
      }
      return "Whole rule";
    },
    recipientList() {
      if (!Array.isArray(this.rule?.recipient_channels)) return [];
      return this.rule.recipient_channels.map((rc) => {
        const name =
          rc.recipient_name || (rc.config && rc.config.phone) || "--";
        const phone =
          rc.config && rc.config.phone ? ` (${rc.config.phone})` : "";
        return name + phone;
      });
    },
    primaryRecipientCount() {
      return this.recipientList.length;
    },
    escalationCards() {
      if (
        !Array.isArray(this.rule?.escalation_steps) ||
        !this.rule.escalation_steps.length
      )
        return [];
      return this.rule.escalation_steps.map((step, idx) => {
        const recipients = Array.isArray(step.recipient_channels)
          ? step.recipient_channels.map(
              (rc) =>
                rc.recipient_name || (rc.config && rc.config.phone) || "--"
            )
          : [];
        return {
          level: idx + 1,
          waitMin: step.after_min,
          recipients,
        };
      });
    },
    templatePreview() {
      if (!this.rule || !this.rule.template) return "";
      return this.renderTemplatePreview(this.rule.template);
    },
  },
  methods: {
    listNotifications,
    formatTimestamp(value) {
      if (!value) return "--";
      let date;
      if (typeof value === "string" && /^\d+$/.test(value)) {
        date = new Date(Number(value));
      } else {
        date = new Date(value);
      }
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleString();
    },
    renderTemplatePreview(template) {
      const sample = {
        eventContent: "Sample alert event",
        eventLevel: (this.eventLevelLabels[0] || "HIGH").toUpperCase(),
        deviceKey: this.eventSources[0] || "device-key",
        assetName: this.eventSources[0] || "Asset",
        ruleName: this.rule?.rule_name || "Notification Rule",
        eventName: this.rule?.rule_name || "Alert",
        timestamp:
          this.lastTriggerLabel !== "--"
            ? this.lastTriggerLabel
            : this.formatTimestamp(Date.now()),
        feedbackUrl: "https://cereb.ai/acknowledge/preview",
        cameraName: "Camera 1",
      };
      return template.replace(/\$\{([^}]+)\}/g, (_, token) => {
        const key = token.split(":")[0].trim();
        return sample[key] || `{{${key}}}`;
      });
    },
    statusBadgeClass(status) {
      switch (status) {
        case "SENT":
        case "DELIVERED":
          return "bg-green-100 text-green-800 border-green-200";
        case "FAILED":
          return "bg-red-100 text-red-800 border-red-200";
        case "PENDING":
          return "bg-orange-100 text-orange-800 border-orange-200";
        default:
          return "bg-gray-100 text-gray-800 border-gray-200";
      }
    },
    historyIconClass(status) {
      switch (status) {
        case "SENT":
        case "DELIVERED":
          return "text-green-500";
        case "FAILED":
          return "text-red-500";
        case "PENDING":
          return "text-orange-500";
        default:
          return "text-slate-400";
      }
    },
    refreshScroll() {
      if (
        this.$refs.smartInfiniteCursorScrollRef &&
        this.$refs.smartInfiniteCursorScrollRef.refresh
      ) {
        this.$refs.smartInfiniteCursorScrollRef.refresh();
      }
    },
  },
  mounted() {
    this.refreshScroll();
  },
};
</script>
